'use client'

import { cva } from 'class-variance-authority'
import NextLink, { LinkProps as NextLinkProps } from 'next/link'
import React from 'react'

import { useConfig } from '../../core'
import { cn } from '../../utils/cn'

export type Variant = 'none' | 'primary' | 'secondary'

export const linkVariants = cva('cursor-pointer no-underline', {
  variants: {
    variant: {
      primary:
        'text-blue-01 underline decoration-auto underline-offset-auto transition-opacity duration-150 ease-in-out typography-subhead1 hover:opacity-50',
      secondary:
        'text-gray-02 underline decoration-auto underline-offset-auto transition-opacity duration-150 ease-in-out typography-body2 hover:opacity-50',
      none: '',
    },
  },
  defaultVariants: {
    variant: 'none',
  },
})

export interface LinkProps
  extends Omit<NextLinkProps, 'onClick' | 'onMouseEnter' | 'onTouchStart'>,
    Omit<React.ComponentPropsWithRef<'a'>, 'href'> {
  /**
   * Link variant
   */
  variant?: Variant

  /**
   * Is an external link?
   */
  external?: boolean

  /**
   * Content of link
   */
  children?: React.ReactNode
}

/**
 * Link component
 * Switches between the raw `a` tag and `next/link`
 */
export const Link = React.forwardRef<HTMLAnchorElement, LinkProps>(
  (
    {
      variant = 'none',
      href,
      as,
      replace,
      scroll,
      shallow,
      prefetch,
      locale,
      external,
      children,
      target,
      className,
      ...rest
    }: LinkProps,
    ref,
  ) => {
    const { nextjs } = useConfig()

    const classes = linkVariants({ variant, className })

    if (nextjs && !nextjs.disableNextLink && !external) {
      // Resolve the path for prefetch decisions (href can be a UrlObject)
      const hrefPath = typeof href === 'string' ? href : href?.pathname

      // Disable prefetch for hrefs the app marks as non-prefetchable
      // (e.g. cross-router links), unless explicitly set via props
      const resolvedPrefetch =
        prefetch === undefined &&
        hrefPath &&
        nextjs.disableLinkPrefetch?.(hrefPath)
          ? false
          : prefetch

      return (
        <NextLink
          ref={ref}
          href={href}
          className={cn(classes)}
          as={as}
          replace={replace}
          scroll={scroll}
          shallow={shallow}
          prefetch={resolvedPrefetch}
          locale={locale}
          target={target}
          {...rest}
        >
          {children}
        </NextLink>
      )
    }

    return (
      <a
        ref={ref}
        className={cn(classes)}
        href={href as string}
        target={target}
        {...rest}
      >
        {children}
      </a>
    )
  },
)

Link.displayName = 'Link'
